import { useState } from 'react'
import type { FormEvent } from 'react'
import { useAuth } from '../auth/ContextoAuth'
import { autenticacion, inventario } from '../api/servicios'
import { cantidad as formatearCantidad, fechaHora, dinero } from '../api/formato'
import type {
    Autorizador,
    MovimientoInventario,
    Producto,
    TipoMovimientoInventario,
} from '../api/tipos'
import { BuscadorProducto } from '../componentes/BuscadorProducto'
import { Dialogo } from '../componentes/Dialogo'
import { Estado } from '../componentes/Estado'
import { Paginador, usarRetardo } from '../componentes/Paginador'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './listados.css'

/** Tipos que un usuario puede registrar a mano, en espejo de SignoMovimiento. */
const TIPOS_MANUALES: {
    valor: TipoMovimientoInventario
    texto: string
    suma: boolean
    exigeCosto: boolean
    exigeAutorizacion: boolean
}[] = [
    { valor: 'Entrada', texto: 'Entrada de mercadería', suma: true, exigeCosto: true, exigeAutorizacion: false },
    { valor: 'AjustePositivo', texto: 'Ajuste positivo', suma: true, exigeCosto: false, exigeAutorizacion: true },
    { valor: 'AjusteNegativo', texto: 'Ajuste negativo', suma: false, exigeCosto: false, exigeAutorizacion: true },
    { valor: 'Merma', texto: 'Merma', suma: false, exigeCosto: false, exigeAutorizacion: true },
    { valor: 'Vencimiento', texto: 'Vencimiento', suma: false, exigeCosto: false, exigeAutorizacion: true },
]

function reglasDe(tipo: TipoMovimientoInventario) {
    return TIPOS_MANUALES.find((t) => t.valor === tipo)
}

export function Inventario() {
    const [pestania, setPestania] = useState<'existencias' | 'movimientos'>('existencias')

    return (
        <div className="pagina-listado columna g4">
            <h1>Inventario</h1>

            <div className="pestanias" role="tablist">
                <button
                    type="button"
                    role="tab"
                    className="pestania"
                    aria-selected={pestania === 'existencias'}
                    onClick={() => setPestania('existencias')}
                >
                    Existencias
                </button>
                <button
                    type="button"
                    role="tab"
                    className="pestania"
                    aria-selected={pestania === 'movimientos'}
                    onClick={() => setPestania('movimientos')}
                >
                    Movimientos
                </button>
            </div>

            {pestania === 'existencias' ? <Existencias /> : <Movimientos />}
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* Existencias                                                                 */
/* -------------------------------------------------------------------------- */

function Existencias() {
    const [texto, setTexto] = useState('')
    const [soloBajoMinimo, setSoloBajoMinimo] = useState(false)
    const [pagina, setPagina] = useState(1)
    const [kardexDe, setKardexDe] = useState<{ productoId: number; nombre: string } | null>(null)

    const busqueda = usarRetardo(texto)

    const existencias = usarCargar(
        () =>
            inventario.existencias({
                texto: busqueda || undefined,
                soloBajoMinimo,
                pagina,
            }),
        [busqueda, soloBajoMinimo, pagina],
    )

    return (
        <section className="panel columna g3">
            <div className="filtros">
                <input
                    className="campo filtro-ancho"
                    type="search"
                    placeholder="Buscar por nombre o SKU"
                    value={texto}
                    onChange={(e) => {
                        setTexto(e.target.value)
                        setPagina(1)
                    }}
                />

                <label className="fila g2 interruptor">
                    <input
                        type="checkbox"
                        checked={soloBajoMinimo}
                        onChange={(e) => {
                            setSoloBajoMinimo(e.target.checked)
                            setPagina(1)
                        }}
                    />
                    <span>Solo bajo mínimo</span>
                </label>
            </div>

            <Estado
                cargando={existencias.cargando}
                error={existencias.error}
                alReintentar={existencias.recargar}
                vacio={(existencias.datos?.items ?? []).length === 0}
                mensajeVacio={
                    soloBajoMinimo
                        ? 'Ningún producto está bajo su stock mínimo.'
                        : 'No hay existencias registradas.'
                }
            >
                <div className="tabla-envoltura">
                    <table className="tabla">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th className="derecha">Existencia</th>
                                <th className="derecha oculto-angosto">Mínimo</th>
                                <th className="oculto-angosto">Actualizado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(existencias.datos?.items ?? []).map((e) => (
                                <tr
                                    key={e.productoId}
                                    className="fila-elegible"
                                    onClick={() =>
                                        setKardexDe({ productoId: e.productoId, nombre: e.nombre })
                                    }
                                >
                                    <td>
                                        <div className="columna">
                                            <span>{e.nombre}</span>
                                            <span className="tenue diminuto numero">{e.sku}</span>
                                        </div>
                                    </td>
                                    <td className="derecha numero">
                                        {formatearCantidad(e.cantidad, 3)} {e.unidadMedidaCodigo}
                                        {e.bajoMinimo && (
                                            <span className="insignia insignia-atencion">bajo</span>
                                        )}
                                    </td>
                                    <td className="derecha numero oculto-angosto">
                                        {formatearCantidad(e.stockMinimo, 3)}
                                    </td>
                                    <td className="oculto-angosto tenue chico">
                                        {fechaHora(e.actualizadoEn)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Estado>

            <Paginador pagina={existencias.datos} alCambiar={setPagina} />

            {kardexDe && (
                <DialogoKardex
                    productoId={kardexDe.productoId}
                    nombre={kardexDe.nombre}
                    alCerrar={() => setKardexDe(null)}
                />
            )}
        </section>
    )
}

function DialogoKardex({
    productoId,
    nombre,
    alCerrar,
}: {
    productoId: number
    nombre: string
    alCerrar: () => void
}) {
    const kardex = usarCargar(() => inventario.kardex(productoId), [productoId])

    return (
        <Dialogo
            titulo={`Kardex · ${nombre}`}
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <button type="button" className="boton" onClick={alCerrar}>
                    Cerrar
                </button>
            }
        >
            <Estado
                cargando={kardex.cargando}
                error={kardex.error}
                alReintentar={kardex.recargar}
                vacio={(kardex.datos?.movimientos ?? []).length === 0}
                mensajeVacio="Este producto no tiene movimientos confirmados."
            >
                <div className="columna g3">
                    <div className="fila entre g3">
                        <span className="tenue chico">Saldo inicial</span>
                        <span className="numero">
                            {formatearCantidad(kardex.datos?.saldoInicial ?? 0, 3)}
                        </span>
                    </div>

                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Movimiento</th>
                                    <th className="derecha">Cantidad</th>
                                    <th className="derecha">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(kardex.datos?.movimientos ?? []).map((m) => (
                                    <tr key={`${m.numero}-${m.fecha}`}>
                                        <td className="chico">{fechaHora(m.fecha)}</td>
                                        <td>
                                            <div className="columna">
                                                <span className="chico">{m.tipo}</span>
                                                <span className="tenue diminuto numero">
                                                    {m.numero}
                                                </span>
                                            </div>
                                        </td>
                                        <td
                                            className={`derecha numero ${
                                                m.cantidad >= 0 ? 'monto-suma' : 'monto-resta'
                                            }`}
                                        >
                                            {formatearCantidad(m.cantidad, 3)}
                                        </td>
                                        <td className="derecha numero">
                                            {formatearCantidad(m.saldoAcumulado, 3)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="fila entre g3">
                        <span className="negrita">Saldo final</span>
                        <span className="numero negrita">
                            {formatearCantidad(kardex.datos?.saldoFinal ?? 0, 3)}
                        </span>
                    </div>
                </div>
            </Estado>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */
/* Movimientos                                                                 */
/* -------------------------------------------------------------------------- */

function Movimientos() {
    const { puede } = useAuth()
    const puedeRegistrar = puede('inventario.entrada')
    const puedeAnular = puede('inventario.ajustar')

    const [tipo, setTipo] = useState('')
    const [estado, setEstado] = useState('')
    const [pagina, setPagina] = useState(1)
    const [nuevo, setNuevo] = useState(false)
    const [abierto, setAbierto] = useState<MovimientoInventario | null>(null)
    const [anulando, setAnulando] = useState<MovimientoInventario | null>(null)

    const movimientos = usarCargar(
        () =>
            inventario.movimientos({
                tipo: tipo || undefined,
                estado: estado || undefined,
                pagina,
            }),
        [tipo, estado, pagina],
    )

    return (
        <section className="panel columna g3">
            <div className="filtros">
                <select
                    className="campo"
                    aria-label="Tipo"
                    value={tipo}
                    onChange={(e) => {
                        setTipo(e.target.value)
                        setPagina(1)
                    }}
                >
                    <option value="">Todos los tipos</option>
                    {TIPOS_MANUALES.map((t) => (
                        <option key={t.valor} value={t.valor}>
                            {t.texto}
                        </option>
                    ))}
                    <option value="Venta">Venta</option>
                    <option value="DevolucionCliente">Devolución de cliente</option>
                </select>

                <select
                    className="campo"
                    aria-label="Estado"
                    value={estado}
                    onChange={(e) => {
                        setEstado(e.target.value)
                        setPagina(1)
                    }}
                >
                    <option value="">Todos los estados</option>
                    <option value="Borrador">Borrador</option>
                    <option value="Confirmado">Confirmado</option>
                    <option value="Anulado">Anulado</option>
                </select>

                {puedeRegistrar && (
                    <button
                        type="button"
                        className="boton boton-primario"
                        onClick={() => setNuevo(true)}
                    >
                        Nuevo movimiento
                    </button>
                )}
            </div>

            <Estado
                cargando={movimientos.cargando}
                error={movimientos.error}
                alReintentar={movimientos.recargar}
                vacio={(movimientos.datos?.items ?? []).length === 0}
                mensajeVacio="No hay movimientos con esos filtros."
            >
                <div className="tabla-envoltura">
                    <table className="tabla">
                        <thead>
                            <tr>
                                <th>Número</th>
                                <th>Tipo</th>
                                <th className="oculto-angosto">Fecha</th>
                                <th className="derecha">Cantidad</th>
                                <th>Estado</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {(movimientos.datos?.items ?? []).map((m) => (
                                <tr key={m.movimientoInventarioId}>
                                    <td className="numero chico">{m.numero}</td>
                                    <td>
                                        <div className="columna">
                                            <span>{reglasDe(m.tipo)?.texto ?? m.tipo}</span>
                                            {m.proveedorNombre && (
                                                <span className="tenue diminuto">
                                                    {m.proveedorNombre}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="oculto-angosto tenue chico">
                                        {fechaHora(m.creadoEn)}
                                    </td>
                                    <td className="derecha numero">
                                        {formatearCantidad(m.cantidadTotal, 3)}
                                    </td>
                                    <td>
                                        <span
                                            className={`insignia ${
                                                m.estado === 'Confirmado'
                                                    ? 'insignia-ok'
                                                    : m.estado === 'Anulado'
                                                      ? 'insignia-peligro'
                                                      : 'insignia-atencion'
                                            }`}
                                        >
                                            {m.estado}
                                        </span>
                                    </td>
                                    <td className="derecha">
                                        <div className="fila g2 acciones-fila">
                                            <button
                                                type="button"
                                                className="boton boton-fantasma"
                                                onClick={() => setAbierto(m)}
                                            >
                                                {m.estado === 'Borrador' && puedeRegistrar
                                                    ? 'Continuar'
                                                    : 'Ver'}
                                            </button>

                                            {puedeAnular && m.estado === 'Confirmado' && (
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma boton-peligro"
                                                    onClick={() => setAnulando(m)}
                                                >
                                                    Anular
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Estado>

            <Paginador pagina={movimientos.datos} alCambiar={setPagina} />

            {nuevo && (
                <DialogoNuevoMovimiento
                    alCreado={(movimiento) => {
                        setNuevo(false)
                        setAbierto(movimiento)
                    }}
                    alCerrar={() => setNuevo(false)}
                />
            )}

            {abierto && (
                <DialogoDetalleMovimiento
                    movimiento={abierto}
                    alCerrar={() => {
                        setAbierto(null)
                        movimientos.recargar()
                    }}
                />
            )}

            {anulando && (
                <DialogoAnular
                    movimiento={anulando}
                    alCerrar={() => setAnulando(null)}
                    alAnulado={() => {
                        setAnulando(null)
                        movimientos.recargar()
                    }}
                />
            )}
        </section>
    )
}

function DialogoNuevoMovimiento({
    alCreado,
    alCerrar,
}: {
    alCreado: (movimiento: MovimientoInventario) => void
    alCerrar: () => void
}) {
    const [tipo, setTipo] = useState<TipoMovimientoInventario>('Entrada')
    const [proveedor, setProveedor] = useState('')
    const [referencia, setReferencia] = useState('')
    const [motivo, setMotivo] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    const reglas = reglasDe(tipo)

    async function crear(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (reglas?.exigeAutorizacion && motivo.trim().length === 0) {
            setError('Este tipo de movimiento exige indicar el motivo.')
            return
        }

        setEnviando(true)

        try {
            alCreado(
                await inventario.crearMovimiento({
                    tipo,
                    motivo: motivo.trim() || undefined,
                    proveedorNombre: proveedor.trim() || undefined,
                    referenciaExterna: referencia.trim() || undefined,
                }),
            )
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Nuevo movimiento"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-nuevo-movimiento"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Creando…' : 'Continuar'}
                    </button>
                </>
            }
        >
            <form id="forma-nuevo-movimiento" className="columna g4" onSubmit={crear}>
                <div>
                    <label className="etiqueta" htmlFor="tipo-movimiento">
                        Tipo
                    </label>
                    <select
                        id="tipo-movimiento"
                        className="campo"
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value as TipoMovimientoInventario)}
                    >
                        {TIPOS_MANUALES.map((t) => (
                            <option key={t.valor} value={t.valor}>
                                {t.texto}
                            </option>
                        ))}
                    </select>
                    <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                        {reglas?.suma ? 'Suma existencia.' : 'Resta existencia.'}
                        {reglas?.exigeAutorizacion &&
                            ' Al confirmarlo se pedirá quién lo autoriza.'}
                    </p>
                </div>

                {tipo === 'Entrada' && (
                    <>
                        <div>
                            <label className="etiqueta" htmlFor="proveedor">
                                Proveedor <span className="tenue">(opcional)</span>
                            </label>
                            <input
                                id="proveedor"
                                className="campo"
                                maxLength={160}
                                value={proveedor}
                                onChange={(e) => setProveedor(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="etiqueta" htmlFor="referencia-externa">
                                N° de factura o guía <span className="tenue">(opcional)</span>
                            </label>
                            <input
                                id="referencia-externa"
                                className="campo"
                                maxLength={80}
                                value={referencia}
                                onChange={(e) => setReferencia(e.target.value)}
                            />
                        </div>
                    </>
                )}

                <div>
                    <label className="etiqueta" htmlFor="motivo-movimiento">
                        Motivo{' '}
                        {!reglas?.exigeAutorizacion && <span className="tenue">(opcional)</span>}
                    </label>
                    <input
                        id="motivo-movimiento"
                        className="campo"
                        maxLength={500}
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

function DialogoDetalleMovimiento({
    movimiento: inicial,
    alCerrar,
}: {
    movimiento: MovimientoInventario
    alCerrar: () => void
}) {
    const { puede } = useAuth()
    const [movimiento, setMovimiento] = useState(inicial)
    const [error, setError] = useState<string | null>(null)
    const [ocupado, setOcupado] = useState(false)

    const [elegido, setElegido] = useState<Producto | null>(null)
    const [cantidad, setCantidad] = useState('')
    const [costo, setCosto] = useState('')
    const [autorizador, setAutorizador] = useState('')

    const reglas = reglasDe(movimiento.tipo)
    const editable = movimiento.estado === 'Borrador' && puede('inventario.entrada')

    const autorizadores = usarCargar<Autorizador[]>(
        () =>
            reglas?.exigeAutorizacion
                ? autenticacion.autorizadores('inventario.autorizar')
                : Promise.resolve([]),
        [reglas?.exigeAutorizacion],
    )

    /** Devuelve si la accion salio bien, para no limpiar el formulario al fallar. */
    async function ejecutar(accion: () => Promise<MovimientoInventario>): Promise<boolean> {
        setError(null)
        setOcupado(true)

        try {
            setMovimiento(await accion())
            return true
        } catch (fallo) {
            setError(mensajeDeError(fallo))
            return false
        } finally {
            setOcupado(false)
        }
    }

    async function agregar(evento: FormEvent) {
        evento.preventDefault()

        if (!elegido) {
            setError('Elija un producto.')
            return
        }

        const valor = Number(cantidad)

        if (!Number.isFinite(valor) || valor <= 0) {
            setError('La cantidad debe ser mayor que cero.')
            return
        }

        const costoUnitario = costo.trim() === '' ? undefined : Number(costo)

        if (reglas?.exigeCosto && (costoUnitario === undefined || !Number.isFinite(costoUnitario))) {
            setError('En una entrada el costo unitario es obligatorio: sin él no se valoriza.')
            return
        }

        const listo = await ejecutar(() =>
            inventario.agregarDetalle(movimiento.movimientoInventarioId, {
                productoId: elegido.productoId,
                cantidad: valor,
                costoUnitario,
            }),
        )

        if (listo) {
            setElegido(null)
            setCantidad('')
            setCosto('')
        }
    }

    async function confirmar() {
        if (reglas?.exigeAutorizacion && !autorizador) {
            setError('Elija quién autoriza el movimiento.')
            return
        }

        await ejecutar(() =>
            inventario.confirmar(
                movimiento.movimientoInventarioId,
                autorizador ? Number(autorizador) : undefined,
            ),
        )
    }

    return (
        <Dialogo
            titulo={`${reglas?.texto ?? movimiento.tipo} · ${movimiento.numero}`}
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cerrar
                    </button>

                    {editable && (
                        <button
                            type="button"
                            className="boton boton-primario"
                            disabled={ocupado || movimiento.detalles.length === 0}
                            onClick={confirmar}
                        >
                            {ocupado ? 'Aplicando…' : 'Confirmar y aplicar'}
                        </button>
                    )}
                </>
            }
        >
            <div className="columna g4">
                <div className="fila entre envuelve g3">
                    <span className="tenue chico">
                        Creado el {fechaHora(movimiento.creadoEn)}
                        {movimiento.proveedorNombre && ` · ${movimiento.proveedorNombre}`}
                    </span>
                    <span
                        className={`insignia ${
                            movimiento.estado === 'Confirmado'
                                ? 'insignia-ok'
                                : movimiento.estado === 'Anulado'
                                  ? 'insignia-peligro'
                                  : 'insignia-atencion'
                        }`}
                    >
                        {movimiento.estado}
                    </span>
                </div>

                {movimiento.motivo && <p className="chico">{movimiento.motivo}</p>}

                {movimiento.detalles.length === 0 ? (
                    <p className="vacio">Todavía no hay líneas en este movimiento.</p>
                ) : (
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th className="derecha">Cantidad</th>
                                    <th className="derecha">Costo</th>
                                    {editable && <th />}
                                </tr>
                            </thead>
                            <tbody>
                                {movimiento.detalles.map((d) => (
                                    <tr key={d.movimientoInventarioDetalleId}>
                                        <td>
                                            <div className="columna">
                                                <span>{d.nombre}</span>
                                                <span className="tenue diminuto numero">{d.sku}</span>
                                            </div>
                                        </td>
                                        <td className="derecha numero">
                                            {formatearCantidad(d.cantidad, 3)}
                                        </td>
                                        <td className="derecha numero">{dinero(d.costoUnitario)}</td>
                                        {editable && (
                                            <td className="derecha">
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma boton-peligro"
                                                    disabled={ocupado}
                                                    onClick={() =>
                                                        ejecutar(() =>
                                                            inventario.quitarDetalle(
                                                                movimiento.movimientoInventarioId,
                                                                d.movimientoInventarioDetalleId,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    Quitar
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {editable && (
                    <form className="columna g3" onSubmit={agregar}>
                        <h3>Agregar producto</h3>

                        {elegido ? (
                            <div className="fila entre g3">
                                <span>{elegido.nombre}</span>
                                <button
                                    type="button"
                                    className="boton boton-fantasma"
                                    onClick={() => setElegido(null)}
                                >
                                    Cambiar
                                </button>
                            </div>
                        ) : (
                            <BuscadorProducto alElegir={setElegido} />
                        )}

                        <div className="filtros">
                            <div>
                                <label className="etiqueta" htmlFor="cantidad-detalle">
                                    Cantidad
                                </label>
                                <input
                                    id="cantidad-detalle"
                                    className="campo campo-numero"
                                    type="number"
                                    min={0}
                                    step="any"
                                    inputMode="decimal"
                                    value={cantidad}
                                    onChange={(e) => setCantidad(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="etiqueta" htmlFor="costo-detalle">
                                    Costo unitario{' '}
                                    {!reglas?.exigeCosto && <span className="tenue">(opcional)</span>}
                                </label>
                                <input
                                    id="costo-detalle"
                                    className="campo campo-numero"
                                    type="number"
                                    min={0}
                                    step={1}
                                    inputMode="numeric"
                                    value={costo}
                                    onChange={(e) => setCosto(e.target.value)}
                                />
                            </div>

                            <button type="submit" className="boton" disabled={ocupado}>
                                Agregar
                            </button>
                        </div>
                    </form>
                )}

                {editable && reglas?.exigeAutorizacion && (
                    <div>
                        <label className="etiqueta" htmlFor="autorizador-inventario">
                            Autoriza
                        </label>
                        <Estado
                            cargando={autorizadores.cargando}
                            error={autorizadores.error}
                            alReintentar={autorizadores.recargar}
                            vacio={(autorizadores.datos ?? []).length === 0}
                            mensajeVacio="No hay usuarios con permiso para autorizar ajustes."
                        >
                            <select
                                id="autorizador-inventario"
                                className="campo"
                                value={autorizador}
                                onChange={(e) => setAutorizador(e.target.value)}
                            >
                                <option value="">Elija quién autoriza…</option>
                                {(autorizadores.datos ?? []).map((a) => (
                                    <option key={a.usuarioId} value={a.usuarioId}>
                                        {a.nombreCompleto}
                                    </option>
                                ))}
                            </select>
                        </Estado>
                    </div>
                )}

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </div>
        </Dialogo>
    )
}

function DialogoAnular({
    movimiento,
    alAnulado,
    alCerrar,
}: {
    movimiento: MovimientoInventario
    alAnulado: () => void
    alCerrar: () => void
}) {
    const [motivo, setMotivo] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function anular(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (motivo.trim().length === 0) {
            setError('La anulación exige indicar el motivo.')
            return
        }

        setEnviando(true)

        try {
            await inventario.anular(movimiento.movimientoInventarioId, motivo.trim())
            alAnulado()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo={`Anular ${movimiento.numero}`}
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-anular"
                        className="boton boton-peligro"
                        disabled={enviando}
                    >
                        {enviando ? 'Anulando…' : 'Anular'}
                    </button>
                </>
            }
        >
            <form id="forma-anular" className="columna g4" onSubmit={anular}>
                <div className="aviso aviso-atencion">
                    Se revierte el efecto del movimiento sobre las existencias. El movimiento
                    queda registrado como anulado, no se borra.
                </div>

                <div>
                    <label className="etiqueta" htmlFor="motivo-anular">
                        Motivo
                    </label>
                    <input
                        id="motivo-anular"
                        className="campo"
                        maxLength={500}
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}
